import { LayoutPages } from "@pdfking/filling-flex"
import { Max, ToNumber, ToSpatial } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"
import type { Media, PageNumber } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import {
  Constant,
  CreateSingleton,
  GuidToString,
  Keys,
  Length,
  Range,
  RecordCreate,
  SortBy,
  UUID,
  Values,
} from "@pdfking/tfunc"

import { DEFAULT_SIZE } from "#constants"
import type { ImpositionSchema } from "#internal"
import type { DestinationID, FileSlots, ImposeResult, PageInfo } from "#public"

import { ColorList } from "../../data/ColorList"
import { CreateSheets } from "../common/CreateSheets"
import { MediaSlots } from "../common/MediaSlots"
import { BuildFilePageLayout } from "./BuildFilePageLayout"
import { CreateFilesBook } from "./CreateFilesBook"

/**
 * The file pass-through: no imposition at all, one page per slot at that
 * page's own trim box. Slot `n` is page `n`, and every destination with an
 * `n`th slot prints it there.
 *
 * Each slot is sized by the first destination (in id order) whose page has
 * been split, and titled by the file it came from; slots nobody has measured
 * yet fall back to the sample size and re-size once a split lands.
 *
 * Every layer but the file one comes from a real grow layout
 * ({@link BuildFilePageLayout}), so a pass-through page gets the same cut
 * outline, crossmarks and name block as an imposed sheet. The file layer is
 * the uploaded page at its own media box: a pass-through shows the PDF as it
 * is instead of scaling it into a cell.
 */
export const ImposeFiles = (
  slots: FileSlots,
  input: Pick<ImpositionSchema, "filename">
): ImposeResult => {
  const ids = SortBy(Keys(slots)) as DestinationID[]
  const count = Max(0, ...Values(slots).map(pages => Length(pages)))

  const resolved = [...Range(1, count)].map(slot => {
    let media: Media | undefined
    let filename: FileName | undefined
    for (const id of ids) {
      const entry = slots[id]![slot as PageNumber]
      if (!entry) continue
      // First destination naming the slot titles it; a later one only takes
      // over if its page has actually been split.
      if (entry.page) {
        media = entry.page
        filename = entry.filename
        break
      }
      filename ??= entry.filename
    }
    return { media, filename }
  })

  // One size for every page we can't measure, so a run of blank pages matches
  // the real pages around it.
  const sample = resolved.find(r => r.media)?.media?.trim ?? DEFAULT_SIZE

  const destinations = RecordCreate(
    ids.map(id => [id, Keys(slots[id]!).map(ToNumber) as PageNumber[]] as const)
  )
  // The cut falls on the trim box (the page rect itself); everything outside
  // it, up to the media box, is bleed — what the media's mask lets through, so
  // a pass-through shows the uploaded page whole, out to its media box.
  const sized = resolved.map(({ media, filename }) => ({
    size: media?.trim ?? sample,
    // the page's own bleed: how far its media box reaches past the trim
    padding: media?.bleed ?? ZERO_MARGINS,
    filename,
  }))

  const pages = RecordCreate(
    sized.map(
      ({ size }, index) =>
        [
          (index + 1) as PageNumber,
          {
            ...size,
            is_front: true,
            print_front: true,
            print_rear: false,
          } satisfies PageInfo,
        ] as const
    )
  )

  const sheets = CreateSheets(
    sized.map(({ size, padding, filename }, index) => {
      const slot = (index + 1) as PageNumber
      return {
        frame: ToSpatial(size),
        bleed: padding,
        destinations: RecordCreate(
          ids.flatMap((id): [DestinationID, PageNumber[]][] =>
            slots[id]![slot] ? [[id, [slot]]] : []
          )
        ),
        divisions: {},
        repetition_number: 1,
        layout: () => LayoutPages(BuildFilePageLayout(size, padding, filename).layout, { size }),
        ctx: {
          side: "front" as const,
          page_number_of: Constant(slot),
          page_informations: undefined,
          bleed: undefined,
          // The grow's page snaps to the layout; the uploaded page is plotted
          // as it is: its trim on the page rect, its media box through the mask.
          media_processor: media => [{ ...media, position: ToSpatial(size), mask: padding }],
          context_processor: ctx => ctx,
        },
      }
    }),
    MediaSlots(destinations)
  )

  return {
    job_id: GuidToString(UUID()),
    input,
    sheets,
    pages,
    destinations,
    fills: [],
    book3d: CreateSingleton(() => CreateFilesBook(Values(pages))),
    color_map: ColorList,
    fields: [],
    informations: [],
    sheet_math: [],
  }
}
